import { loadConnectAndInitialize } from '@stripe/connect-js';
import { CallWithAuth } from '../../actions/apiAction';
import {
  CREATOR_CONNECT_ACCOUNT_SESSION,
  CREATOR_CONNECT_STATUS,
} from '../../utils/apiUrl';

/** Reuse Connect.js instances across navigations (dashboard → payment setup). */
const instanceCache = {
  dashboard: null,
  onboarding: null,
};

/** Short-lived account-session secrets so Stripe auth is warm before the page opens. */
const secretCache = {
  dashboard: null,
  onboarding: null,
};

const SECRET_TTL_MS = 8 * 60 * 1000;

const appearance = {
  overlays: 'dialog',
  variables: {
    colorPrimary: '#ec3fa0',
  },
};

async function resolvePublishableKey(publishableKey) {
  if (publishableKey) return publishableKey;
  const statusRes = await CallWithAuth('GET', CREATOR_CONNECT_STATUS);
  const key = statusRes?.data?.publishable_key;
  if (!key) {
    throw new Error(
      'Stripe publishable key is not configured on the API (STRIPE_PUBLISHABLE_KEY).'
    );
  }
  return key;
}

async function createAccountSessionSecret(purpose) {
  const res = await CallWithAuth('POST', CREATOR_CONNECT_ACCOUNT_SESSION, {
    purpose,
  });
  if (res?.status === 200 && res?.data?.client_secret) {
    return res.data.client_secret;
  }
  const detail =
    res?.data?.detail ||
    res?.data?.error ||
    'Failed to create Stripe account session';
  throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
}

function makeFetchClientSecret(purpose) {
  return async () => {
    const cached = secretCache[purpose];
    if (cached?.secret && Date.now() - cached.at < SECRET_TTL_MS) {
      return cached.secret;
    }
    const secret = await createAccountSessionSecret(purpose);
    secretCache[purpose] = { secret, at: Date.now() };
    return secret;
  };
}

/**
 * Get or create a Stripe Connect.js instance for the given purpose.
 * Safe to call from dashboard warmup and from ConnectProvider.
 */
export async function getOrCreateConnectInstance({
  purpose = 'dashboard',
  publishableKey,
} = {}) {
  const existing = instanceCache[purpose];
  if (existing?.instance) return existing.instance;
  if (existing?.promise) return existing.promise;

  const promise = (async () => {
    const key = await resolvePublishableKey(publishableKey);
    const instance = loadConnectAndInitialize({
      publishableKey: key,
      fetchClientSecret: makeFetchClientSecret(purpose),
      appearance,
    });
    instanceCache[purpose] = { instance, promise: null };
    return instance;
  })();

  instanceCache[purpose] = { instance: null, promise };

  try {
    return await promise;
  } catch (err) {
    instanceCache[purpose] = null;
    throw err;
  }
}

/**
 * Prefetch account session + Connect.js on the dashboard so Payment Setup opens warm.
 * No-op if already warming/ready. Failures are non-blocking.
 */
export function warmConnectDashboard(publishableKey) {
  const purpose = 'dashboard';
  const work = (async () => {
    await getOrCreateConnectInstance({ purpose, publishableKey });
    // Prime the account session before embedded components mount.
    const cached = secretCache[purpose];
    if (!cached?.secret || Date.now() - cached.at >= SECRET_TTL_MS) {
      const secret = await createAccountSessionSecret(purpose);
      secretCache[purpose] = { secret, at: Date.now() };
    }
  })();

  work.catch((err) => {
    console.warn('Connect warmup failed', err);
  });

  return work;
}

export function clearConnectCache() {
  instanceCache.dashboard = null;
  instanceCache.onboarding = null;
  secretCache.dashboard = null;
  secretCache.onboarding = null;
}
